<template>
  <section v-for="field in fields" :class="className(field)">
    <h4 v-if="mode === 'input'" :class="titleClassName(field)">
      {{field.name}}
      <label v-if="field.isRequired"
             class="text-danger fw-bold user-select-none">*</label>
    </h4>
    <h5 v-if="mode === 'inputSmall'" :class="titleClassName(field)">
      {{field.name}}
      <label v-if="field.isRequired"
             class="text-danger fw-bold user-select-none">*</label>
    </h5>
    <label v-if="mode === 'search'" :class="titleClassName(field)">
      {{field.name}}
    </label>
    <input v-if="field.format === 'textbox' || field.format === 'textarea'"
           type="text" class="form-control" :class="formClassName(field)"
           v-on:change="inputValue(field)" />
    <div v-if="field.format === 'select'" role="button" class="form-control position-relative" :class="formClassName(field)"
         v-on:click="focusField(field)">
      <span class="text-muted" v-if="selectedItems(field).length === 0">
        {{$t("master_fields_input.click_to_select")}}
      </span>

      <div class="btn-group m-1" v-for="selectedData in selectedItems(field)">
        <button type="button" class="btn btn-sm btn-primary text-start text-break">{{selectedData.data}}</button>
        <button type="button" class="btn btn-sm btn-primary" aria-label="Close" v-on:click.stop="removeSelectedValue(field, selectedData.id)">
          <span class="btn-close btn-close-white"></span>
        </button>
      </div>

      <div v-if="isLoadingData(field)"
           class="position-absolute top-50 start-50 translate-middle w-100 h-100 d-flex align-items-center justify-content-center bg-light opacity-50">
        <div class="spinner-border"></div>
      </div>
      <div v-if="focusedField === field"
           style="z-index: 1;"
           class="position-absolute top-100 start-0 min-w-100 max-vh-50 overflow-auto border rounded-2 bg-body shadow-sm my-1 px-2"
           ref="'focusedField' + field.id">
        <div class="input-group sticky-top py-2 bg-body border-bottom">
          <input type="text" class="form-control"
                 v-model="searchText" />
          <button type="button" class="btn btn-outline-secondary" aria-label="Close" v-on:click.stop="resetSearchText()">
            <span class="btn-close"></span>
          </button>
        </div>
        <div class="container position-relative">
          <div class="row" v-for="fieldData in focusedFieldData"
               v-on:click="toggleSelectValue(field, fieldData.id)">
            <div class="col-1 d-flex align-items-center justify-content-center p-0">
              <svgicon v-if="isSelectedValue(field, fieldData.id)"
                       name="check" class="w-100 h-auto" />
            </div>
            <div class="col py-2 text-break d-flex align-items-center flex-wrap">
              <span>
                {{fieldData.data}}
              </span>
            </div>
          </div>
          <div v-if="!focusedFieldData || focusedFieldData.length == 0"
               class="text-muted">
            {{$t("errors.not_found")}}
          </div>
          <div v-if="isLoadingData(field)"
               class="position-absolute top-50 start-50 translate-middle w-100 h-100 d-flex align-items-center justify-content-center bg-light opacity-50">
            <div class="spinner-border"></div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="field.format === 'number'">
      <template v-if="mode === 'input'">
        <input type="number" class="form-control" :class="formClassName(field)"
               v-on:change="inputValue(field)" />
      </template>
      <template v-else>
        <div class="input-group" :class="formClassName(field)">
          <input type="number" class="form-control"
                 v-on:change="setRangeStart(field)" />
          <span class="input-group-text">
            ～
          </span>
          <input type="number" class="form-control"
                 v-on:change="setRangeEnd(field)" />
        </div>
      </template>
    </template>
    <template v-if="field.format === 'date'">
      <template v-if="mode === 'input'">
        <input type="date" class="form-control" :class="formClassName(field)"
               v-on:change="inputValue(field)" />
      </template>
      <template v-else>
        <div class="input-group" :class="formClassName(field)">
          <input type="date" class="form-control"
                 v-on:change="setRangeStart(field)" />
          <span class="input-group-text">
            ～
          </span>
          <input type="date" class="form-control"
                 v-on:change="setRangeEnd(field)" />
        </div>
      </template>
    </template>
  </section>
</template>

<script>
  import { defineComponent, nextTick } from "vue";
  import { xhrRequest, xhrErrors } from "utils/xhr";
  import { newUuid } from "utils/uuid";
  import { Dropdown } from "bootstrap";
  import Spinner from "utils/spinner";

  export default defineComponent({
    data: () => {
      return {
        fields: [],
        focusedFieldData: [],
        focusedFieldId: null,
        focusedField: null,
        searchText: null,
        dataLoadingFieldId: [],
        selectedMasterDataValue: [],
      }
    },
    props: {
      class: {
        type: String,
      },
      titleClass: {
        type: String,
      },
      formClass: {
        type: String,
      },
      dateClass: {
        type: String,
      },
      dateTitleClass: {
        type: String,
      },
      dateFormClass: {
        type: String,
      },
      numberClass: {
        type: String,
      },
      numberTitleClass: {
        type: String,
      },
      numberFormClass: {
        type: String,
      },
      selectClass: {
        type: String,
      },
      selectTitleClass: {
        type: String,
      },
      selectFormClass: {
        type: String,
      },
      textareaClass: {
        type: String,
      },
      textareaTitleClass: {
        type: String,
      },
      textareaFormClass: {
        type: String,
      },
      textboxClass: {
        type: String,
      },
      textboxTitleClass: {
        type: String,
      },
      textboxFormClass: {
        type: String,
      },
      mode: {
        type: String,
        default: "input",
        validator: (val) => {
          return [null, "input", "inputSmall", "search"].includes(val);
        }
      }
    },
    computed: {
    },
    methods: {
      /* Public */
      getSelectedMasterDataValue() {
        return this.selectedMasterDataValue;
      },
      getQueryData() {
        return this.selectedMasterDataValue;
      },
      getUserAdditionalDataValue() {
        return this.selectedMasterDataValue;
      },
      loadMasterFields() {
        let spinner = new Spinner();
        xhrRequest(
          "GET",
          "MasterFields",
          null,
          {
            success: data => {
              this.fields = data.list;
            },
            error: (xhr, status, error) => { },
            complete: () => {
              spinner.stop();
            },
          },
        );
      },
      loadMasterData(field) {
        if (field == null || field.id == null) return;
        let uuid = newUuid();
        this.addLoadingData(field.id, uuid);
        xhrRequest(
          "GET",
          "MasterData",
          { masterFieldId: field.id, searchText: this.searchText, },
          {
            success: data => {
              this.focusedFieldData = data.list;
            },
            error: (xhr, status, error) => { },
            complete: () => {
              this.removeLoadingData(field.id, uuid);
            },
          },
        );
      },
      className(target = null) {
        if (target == null || target.format == null || this[`${target.format}Class`] == null) return this.class;
        return this[`${target.format}Class`];
      },
      titleClassName(target = null) {
        if (target == null || target.format == null || this[`${target.format}TitleClass`] == null) return this.titleClass;
        return this[`${target.format}TitleClass`];
      },
      formClassName(target = null) {
        if (target == null || target.format == null || this[`${target.format}FormClass`] == null) return this.formClass;
        return this[`${target.format}FormClass`];
      },
      selectedItems(target = null) {
        if (target == null || target.id == null) return [];
        return this.selectedMasterDataValue.find(f => f.id === target.id)?.masterData ?? [];
      },
      isSelectedValue(target = null, id) {
        let v = this.selectedMasterDataValue.find(f => f.id === target?.id)?.masterData?.some(d => d.id === id);
        return v ?? false;
      },
      hasSelectedMasterField(target) {
        if (target == null || target.id == null) return false;
        return this.selectedMasterDataValue.some(f => f.id === target.id);
      },
      addSelectedMasterField(target) {
        if (target == null || target.id == null) return;
        this.selectedMasterDataValue.push({
          id: target.id,
          name: target.name,
          format: target.format,
          description: target.description,
          isRequired: target.isRequired,
          isEnabled: target.isEnabled,
          masterData: target.format === "select" ? [] : {},
        });
      },
      inputValue(target) {
        let v = event?.target?.value;
        if (!this.hasSelectedMasterField(target)) this.addSelectedMasterField(target);
        if (!v) {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.searchText = null;
        } else {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.searchText = v;
        }
      },
      setRangeStart(target) {
        let v = event?.target?.value;
        if (!this.hasSelectedMasterField(target)) this.addSelectedMasterField(target);
        if (!v) {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.start = null;
        } else {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.start = v;
        }
      },
      setRangeEnd(target) {
        let v = event?.target?.value;
        if (!this.hasSelectedMasterField(target)) this.addSelectedMasterField(target);
        if (!v) {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.end = null;
        } else {
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.end = v;
        }
      },
      toggleSelectValue(target, id) {
        if (target == null || target.id == null) return;
        if (this.hasSelectedMasterField(target)) {
          if (this.selectedMasterDataValue.find(f => f.id === target.id).masterData.some(d => d.id === id)) {
            this.removeSelectedValue(target, id);
          } else {
            this.selectedMasterDataValue.find(f => f.id === target.id).masterData.push(this.focusedFieldData.find(d => d.id === id));
          }
        } else {
          this.addSelectedMasterField(target);
          this.selectedMasterDataValue.find(f => f.id === target.id).masterData.push(this.focusedFieldData.find(d => d.id === id));
        }
      },
      removeSelectedValue(target, id) {
        if (target == null || target.id == null) return;
        this.selectedMasterDataValue.find(f => f.id === target.id).masterData = this.selectedMasterDataValue.find(f => f.id === target.id).masterData.filter(d => d.id !== id);
      },
      resetSearchText() {
        this.searchText = null;
      },
      isLoadingData(target = null) {
        if (target == null || target.id == null) return false;
        return this.dataLoadingFieldId.some(d => d.id === target.id);
      },
      addLoadingData(id, uuid) {
        this.dataLoadingFieldId.push({ id: id, uuid: uuid });
      },
      removeLoadingData(id, uuid) {
        this.dataLoadingFieldId = this.dataLoadingFieldId.filter(d => !(d.id === id && d.uuid === uuid));
      },
      focusField(target) {
        event.stopPropagation();
        if (target == null || target.id == null) {
          this.focusedFieldId = null;
          return;
        };
        if (this.focusedFieldId === target.id) return;
        this.focusedFieldData = [];
        this.focusedField = target;
        this.focusedFieldId = target.id;
        this.loadMasterData(target);
        window.addEventListener("click", this.unfocusField);
      },
      unfocusField() {
        this.focusedFieldId = null;
        this.focusedField = null;
        this.focusedFieldData = [];
        this.resetSearchText();
        window.removeEventListener("click", this.unfocusField);
      },
    },
    created() {
      this.loadMasterFields();
    },
    mounted() {

    },
    watch: {
      searchText: function () {
        this.loadMasterData(this.focusedField);
      },
    }
  })

</script>
